import { Reference } from './ReferenceTypes';

/**
 * Lightweight Person type for efficient table views
 * Maps from SCIM attributes to a simplified, consistent interface
 */
export interface Person {
    /** Display name (from SCIM DisplayName) */
    DisplayName: string;

    /** Username (from SCIM UserPrincipalName) */
    Username: string;

    /** UserPrincipalName (from SCIM UserPrincipalName) */
    UserPrincipalName?: string;

    /** Email address (from SCIM Email) */
    Email: string;

    /** Job code (from SCIM JobCode) - used for Applicants instead of Email */
    JobCode?: string;

    /** Entity type classification */
    EntityType: string | Reference; // Can be string or reference object

    /** Unique identifier (from SCIM Id) */
    Id: string;

    /** Available actions (future: string array, currently not in API) */
    Actions?: string[];

    /** Raw JSON data from the API for debug views */
    rawData?: any;
}

/**
 * SCIM attributes needed for Person type
 * Used in API queries to reduce server load
 */
export const PERSON_SCIM_ATTRIBUTES = [
    'DisplayName',
    'UserPrincipalName',
    'Email',
    'JobCode',
    'EntityType',
    'Id',
    'Photo'
] as const;

/** All Users view: same as above but without JobCode (no column shown for it). */
export const PERSON_SCIM_ATTRIBUTES_ALL_USERS = [
    'DisplayName',
    'UserPrincipalName',
    'Email',
    'EntityType',
    'Id',
    'Photo'
] as const;

/**
 * Maps SCIM user data to Person type
 */
export function mapScimUserToPerson(scimUser: any): Person {
    // Import the reference type utility
    const { extractReferenceValue } = require('../utils/referenceTypeUtils');

    return {
        DisplayName: scimUser.DisplayName || '',
        Username: scimUser.UserPrincipalName || '',
        UserPrincipalName: scimUser.UserPrincipalName || undefined,
        Email: scimUser.Email || '',
        JobCode: scimUser.JobCode || '',
        EntityType: extractReferenceValue(scimUser.EntityType, 'EntityType'),
        Id: scimUser.Id || '',
        Actions: undefined, // Not yet available in API
        rawData: scimUser // Store the complete raw data for debug views
    };
}

/**
 * Response type for Person-based API calls
 */
export interface PersonsResponse {
    Resources: Person[];
    totalResults: number;
    startIndex: number;
    itemsPerPage: number;
    /** List-level $ and other response props (e.g. $timeTaken, $caller) */
    [key: string]: any;
}
